/**
 * Exclusive lock for writers that must not share `updated_at` with anyone else
 * (blueprint underlay, geometry import). Design saves do not take this lock for
 * the whole enrich — they take a {@link acquireDesignSaveHold design-save hold}
 * so further design edits can overlap, while this lock still waits until those
 * holds finish.
 *
 * Do not use a depth counter for reentrancy: the JPEG `await` would still look
 * held and let a map-draw snapshot run in the middle of the upload.
 */
let mutex: Promise<void> = Promise.resolve();
let designSaveHolds = 0;
const designSaveHoldWaiters: Array<() => void> = [];

/**
 * Queue of {@link useUpdateBuildingDesign} calls. Released after the optimistic
 * design is in the store so a newer edit can cancel the in-flight enrich
 * (DEV-3785). Callers still await the full save.
 */
let buildingDesignPersistQueue: Promise<void> = Promise.resolve();

/**
 * Run {@link task} after {@link previous} settles.
 * The next waiter starts when {@link task} calls `release`, or when
 * {@link task} settles if it never releases early. The returned `result`
 * still waits for {@link task}.
 */
const beginSerializedTask = <T>(
  previous: Promise<unknown>,
  task: (release: () => void) => Promise<T>,
): { result: Promise<T>; tail: Promise<void> } => {
  let resolveRelease: () => void = () => undefined;
  let didRelease = false;
  const released = new Promise<void>((resolve) => {
    resolveRelease = resolve;
  });
  const release = (): void => {
    if (didRelease) return;
    didRelease = true;
    resolveRelease();
  };
  // A synchronous throw (superseded snapshot) never reaches `.finally`.
  const invoke = (): Promise<T> => {
    try {
      return task(release).finally(release);
    } catch (error) {
      release();
      return Promise.reject(error);
    }
  };
  const result = previous.then(invoke, invoke);
  const tail = previous
    .then(
      () => released,
      () => released,
    )
    .then(
      () => undefined,
      () => undefined,
    );
  return { result, tail };
};

/**
 * Wait until no design-save hold is active.
 * A hold that arrives after this exclusive turn has queued waits behind it.
 */
const waitForDesignSaveHolds = (): Promise<void> => {
  if (designSaveHolds === 0) return Promise.resolve();
  return new Promise((resolve) => {
    designSaveHoldWaiters.push(resolve);
  });
};

/**
 * Wake exclusive waiters once the last design-save hold has finished.
 */
const notifyDesignSaveHoldWaiters = (): void => {
  if (designSaveHolds !== 0) return;
  const waiters = designSaveHoldWaiters.splice(0);
  for (const resolve of waiters) resolve();
};

/**
 * Hold a slot that blocks {@link runSerializedDesignPersist} until released.
 * Waits out an exclusive persist already in progress. Other design-save holds
 * may overlap, so a newer Add storey can enrich while this one is still saving.
 * @returns Release function. Idempotent.
 */
export const acquireDesignSaveHold = (): Promise<() => void> =>
  mutex.then(() => {
    designSaveHolds += 1;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      designSaveHolds -= 1;
      notifyDesignSaveHoldWaiters();
    };
  });

/**
 * Run {@link task} after any in-flight exclusive design persist finishes and
 * no design-save hold is active.
 * Call `releaseLock` once the next exclusive writer may start. Omitting that
 * call holds the lock until {@link task} settles.
 * Previous failure does not block the next task.
 */
export const runSerializedDesignPersist = <T>(
  task: (releaseLock: () => void) => Promise<T>,
): Promise<T> => {
  // Check holds inside the same turn that starts {@link task} so an idle lock
  // still begins on the next microtask, matching callers that flush once.
  const { result, tail } = beginSerializedTask(mutex, (releaseLock) => {
    if (designSaveHolds === 0) return task(releaseLock);
    return waitForDesignSaveHolds().then(() => task(releaseLock));
  });
  mutex = tail;
  return result;
};

/**
 * Serialize design PUTs so a later writer re-reads after the previous optimistic
 * update. `releaseQueue` lets the next edit start before enrich and the server
 * PUT finish, so the newer enrich cancels the one in flight.
 */
export const enqueueBuildingDesignPersist = <T>(
  task: (releaseQueue: () => void) => Promise<T>,
): Promise<T> => {
  const { result, tail } = beginSerializedTask(
    buildingDesignPersistQueue,
    task,
  );
  buildingDesignPersistQueue = tail;
  return result;
};

/**
 * Drop a leftover chain between tests so a hung persist cannot deadlock later specs.
 */
export const resetSerializedDesignPersistForTests = (): void => {
  mutex = Promise.resolve();
  designSaveHolds = 0;
  designSaveHoldWaiters.length = 0;
};

/**
 * Drop a leftover design-update queue between tests.
 */
export const resetBuildingDesignPersistQueueForTests = (): void => {
  buildingDesignPersistQueue = Promise.resolve();
};
